import type { Route } from './+types/areas.$slug';
import { Link, useParams } from 'react-router';
import { LeafMark } from '~/components/marketing/leaf-mark';
import { Breadcrumbs, breadcrumbSchemaItems } from '~/components/breadcrumbs';
import { RelatedPages } from '~/components/related-pages';
import { buildMeta, breadcrumbSchema, faqPageSchema } from '~/lib/seo';
import { SITE } from '~/data/site';
import { AREA_BY_SLUG } from '~/data/areas';

// Neighborhood data comes from the ONE source — src/app/data/areas.ts. It used
// to be a second hand-maintained copy here, which is how the sitemap ended up
// listing ten /areas/<city> pages this file had no entry for.
const HOOD_DATA = AREA_BY_SLUG;

/*
 * A slug with no entry in AREAS must return a real HTTP 404.
 *
 * It used to render "Area not found" with HTTP 200 — a soft-404. Ten of those
 * (/areas/tampa, /areas/clearwater, /areas/sarasota …) were in the sitemap at
 * priority 0.8, so Google was told they were real pages. They are out of the
 * sitemap now; this makes the status honest for anything that reaches them by
 * link or from the index.
 */
export function loader({ params }: Route.LoaderArgs) {
  if (!AREA_BY_SLUG[params.slug ?? '']) {
    throw new Response('Not Found', { status: 404 });
  }
  return null;
}

export const meta: Route.MetaFunction = ({ params }) => {
  const hood = HOOD_DATA[params.slug ?? ''];
  const name = hood?.name ?? 'This area';
  return buildMeta({
    title: `Cleaning in ${name}`,
    description: `Professional residential cleaning in ${name}, St. Petersburg FL. Same crew, same standard, every visit.`,
    path: `/areas/${params.slug}`,
    jsonLd: breadcrumbSchema([
      { name: 'Home', url: `${SITE.url}/` },
      { name: 'Service Areas', url: `${SITE.url}/service-areas` },
      { name, url: `${SITE.url}/areas/${params.slug}` },
    ]),
  });
};

export default function AreaSlugPage() {
  const { slug } = useParams();
  const hood = HOOD_DATA[slug ?? ''];

  if (!hood) {
    return (
      <main className="section">
        <div className="container-narrow">
          <h1 className="display-md">We don&rsquo;t have a page for that area yet</h1>
          <p className="lede chapter-lede">
            We may not have a page for this area yet.{' '}
            <Link to="/service-areas" className="btn-link">See all service areas →</Link>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main>
      <Breadcrumbs path={`/areas/${slug}`} pageLabel={hood?.name ?? 'This area'} />
      <section className="section">
        <div className="container-narrow">
          <Link to="/service-areas" className="btn btn-link" style={{ marginBottom: '24px', display: 'inline-block' }}>
            ← All service areas
          </Link>
          <p className="eyebrow page-eyebrow">St. Petersburg · {hood.zip}</p>
          <h1 className="display-lg page-h1-full">
            Cleaning in{' '}
            <em className="italic-display">{hood.name}</em>.
          </h1>
        </div>
      </section>

      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          {/* Neighborhood banner. Deliberately NOT the leaf-mark-over-photo
              treatment that was removed from /services — the photo carries the
              place, the name sits on it. */}
          <figure className="city-banner reveal">
            <img
              src={hood.photo}
              alt={hood.photoAlt}
              width={1600}
              height={900}
              loading="lazy"
            />
            <figcaption className="city-banner-name">
              {hood.name}
              <span className="city-banner-zip">St. Petersburg &middot; {hood.zip}</span>
            </figcaption>
          </figure>

          <div className="prose reveal">
            <p>{hood.body}</p>
            {/* A per-neighborhood client quote belongs here. Until we have one
                per area, nothing renders — a visible "[paste from Google]"
                placeholder was shipping to production. */}
          </div>
        </div>
      </section>

      <RelatedPages path={`/areas/${slug}`} />

      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <h2>Your {hood.name} home, <em>quietly kept</em>.</h2>
          <p className="cta-lede">See your exact price in under a minute.</p>
          <Link to="/quote" className="btn btn-brass btn-lg">See your price →</Link>
        </div>
      </section>
    </main>
  );
}
